import React, { useState } from 'react';

import AdminsApi from '../../../Api/CRM/Modules/AdminsApi';
import DatabaseLogApi from '../../../Api/CRM/Modules/DatabaseLogApi';
import AdminActivity, { AdminAction } from '../../../Api/Models/AdminActivity';
import { formatDateStringWithTimezone } from '../../../utils/formatters';
import ProtectedLink from '../../elements/ProtectedLink';
import Search from '../../elements/Search';
import Card from '../../elements/common/Card';
import FilterByColumn, { Filter } from '../../elements/common/FilterByColumn';
import ShowMoreBlock from '../../elements/common/ShowMoreBlock';
import { TableCell as Cell } from '../../elements/common/TableDetailsRow';
import TableHead from '../../elements/common/TableHead';
import TableLoader from '../../elements/common/TableLoader';
import { SelectOptions } from '../../elements/formElements/Select';
import Paginator from '../../elements/paginator';
import useAutoPaginator from '../../hooks/useAutoPaginator';
import useOnce from '../../hooks/useOnce';

import { PERMISSION } from 'crm/helpers/Permissions';
import { createOptionsFromEnum } from 'crm/helpers/formUtils';

const adminActions = createOptionsFromEnum(AdminAction);

function AdminActivityLogs() {
    const { items, isLoading, auto } = useAutoPaginator<AdminActivity>((page, params) => {
        return DatabaseLogApi.getAdminActivities(page, { params });
    });

    const [adminNames, setAdminNames] = useState<SelectOptions>({});
    const [modelNames, setModelNames] = useState<SelectOptions>({});

    useOnce(() => {
        AdminsApi.getNames().then(setAdminNames);
        DatabaseLogApi.getModelNames().then(setModelNames);
    });

    return (
        <Card>
            <Search {...auto.search} />
            <FilterByColumn {...auto.filter}>
                <Filter column="created_at" label="Time" type="dateRange" />
                <Filter column="admin_id" label="Admin" type="select" options={adminNames} />
                <Filter column="action" type="select" options={adminActions} />
                <Filter column="loggable_type" label="Object" type="select" options={modelNames} />
            </FilterByColumn>
            <TableLoader isLoading={isLoading}>
                <table className="table model-list">
                    <TableHead>
                        <th>Time</th>
                        <th>IP address</th>
                        <th>User agent</th>
                        <th>Admin</th>
                        <th>Action</th>
                        <th>Object</th>
                        <th>Details</th>
                    </TableHead>
                    <tbody>
                        {items.map((activity) => (
                            <tr key={activity.id}>
                                <td>{formatDateStringWithTimezone(activity.created_at)}</td>
                                <td>{activity.ip_address}</td>
                                <td>{activity.user_agent}</td>
                                <td>
                                    {adminNames[activity.admin_id] ? (
                                        <ProtectedLink
                                            url={`/admins/${activity.admin_id}`}
                                            value={adminNames[activity.admin_id]}
                                            permissions={[PERMISSION.VIEW_ADMIN]}
                                        />
                                    ) : (
                                        activity.admin_id
                                    )}
                                </td>
                                <td>{activity.action}</td>
                                <Cell showIf={activity?.loggable_type?.length > 0}>
                                    {`${activity?.loggable_type?.match(/[^\\]+$/)} (${activity?.loggable_id})`}
                                </Cell>
                                <Cell showIf={!!activity.action_details}>
                                    <ShowMoreBlock>
                                        <pre>{JSON.stringify(activity.action_details, null, 2)}</pre>
                                    </ShowMoreBlock>
                                </Cell>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <Paginator {...auto.paginator} />
            </TableLoader>
        </Card>
    );
}

export default AdminActivityLogs;
